import Cookies from 'js-cookie';

// Rate Limiter для защиты от подбора номеров заказов
class RateLimiter {
  constructor() {
    this.attempts = new Map();
    this.blockedIPs = new Set();
  }

  // Получение стабильного клиентского ID
  getClientID() {
    // Сначала пробуем получить из cookies
    let clientId = Cookies.get('clientId');
    
    if (!clientId) {
      // Если нет в cookies, пробуем localStorage
      clientId = localStorage.getItem('clientId');
      
      if (!clientId) {
        // Генерируем новый ID
        clientId = `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }
      
      // Сохраняем в оба места
      Cookies.set('clientId', clientId, { expires: 365 }); // 1 год
      localStorage.setItem('clientId', clientId);
    }
    
    return clientId;
  }

  // Проверка блокировки через cookies
  isBlockedByCookies() {
    const blockedUntil = Cookies.get('blockedUntil');
    if (!blockedUntil) return false;
    
    const now = Date.now();
    const blockTime = parseInt(blockedUntil);
    
    if (now < blockTime) {
      return true; // Все еще заблокирован
    } else {
      // Время блокировки истекло, очищаем cookie
      Cookies.remove('blockedUntil');
      return false;
    }
  }

  // Проверка лимита для IP
  checkLimit(ip, maxAttempts = 5, windowMs = 15 * 60 * 1000) {
    const now = Date.now();
    const key = `ip_${ip}`;
    
    // Проверка блокировки
    if (this.blockedIPs.has(ip)) {
      throw new Error('IP_BLOCKED');
    }

    const userAttempts = this.attempts.get(key) || { 
      count: 0, 
      resetTime: now + windowMs,
      lastAttempt: 0,
      hasUsedOnce: false, // Флаг использования
      usedAt: null // Время первого использования
    };

    // Сброс счетчика если окно истекло
    if (now > userAttempts.resetTime) {
      userAttempts.count = 0;
      userAttempts.resetTime = now + windowMs;
      userAttempts.hasUsedOnce = false; // Сброс флага при новом окне
      userAttempts.usedAt = null; // Сброс времени использования
    }

    // Проверка лимита
    if (userAttempts.count >= maxAttempts) {
      // Блокировка IP при превышении лимита
      this.blockIP(ip, 'RATE_LIMIT_EXCEEDED');
      throw new Error('RATE_LIMIT_EXCEEDED');
    }

    // Обновление счетчика
    userAttempts.count++;
    userAttempts.lastAttempt = now;
    
    // Отмечаем первое использование
    if (!userAttempts.hasUsedOnce) {
      userAttempts.hasUsedOnce = true;
      userAttempts.usedAt = now; // Запоминаем время первого использования
      
      // Сохраняем информацию о блокировке в cookies для дополнительной защиты
      Cookies.set('blockedUntil', (now + (3 * 60 * 1000)).toString(), { expires: 1 }); // 1 день
    }
    
    this.attempts.set(key, userAttempts);

    return {
      remaining: maxAttempts - userAttempts.count,
      resetTime: userAttempts.resetTime,
      hasUsedOnce: userAttempts.hasUsedOnce,
      usedAt: userAttempts.usedAt
    };
  }

  // Блокировка IP
  blockIP(ip, reason) {
    this.blockedIPs.add(ip);
    console.warn(`IP ${ip} blocked: ${reason}`);
    
    // Автоматическая разблокировка через 1 час
    setTimeout(() => {
      this.blockedIPs.delete(ip);
      console.log(`IP ${ip} unblocked`);
    }, 60 * 60 * 1000);
  }

  // Получение информации о попытках
  getAttemptsInfo(ip) {
    const key = `ip_${ip}`;
    const userAttempts = this.attempts.get(key);
    
    if (!userAttempts) {
      return { count: 0, remaining: 5, resetTime: null, hasUsedOnce: false, usedAt: null };
    }

    const now = Date.now();
    const remaining = Math.max(0, 5 - userAttempts.count);
    const resetTime = userAttempts.resetTime > now ? userAttempts.resetTime : null;

    // Проверяем, истекло ли время блокировки (3 минуты)
    const blockDuration = 3 * 60 * 1000; // 3 минуты в миллисекундах
    const isBlockExpired = userAttempts.usedAt && (now - userAttempts.usedAt) > blockDuration;

    return {
      count: userAttempts.count,
      remaining,
      resetTime,
      isBlocked: this.blockedIPs.has(ip),
      hasUsedOnce: userAttempts.hasUsedOnce || false,
      usedAt: userAttempts.usedAt,
      isBlockExpired,
      timeUntilUnblock: userAttempts.usedAt ? Math.max(0, blockDuration - (now - userAttempts.usedAt)) : 0
    };
  }

  // Очистка старых записей
  cleanup() {
    const now = Date.now();
    for (const [key, value] of this.attempts.entries()) {
      if (now > value.resetTime) {
        this.attempts.delete(key);
      }
    }
  }
}

// Создание глобального экземпляра
const rateLimiter = new RateLimiter();

// Очистка каждые 5 минут
setInterval(() => {
  rateLimiter.cleanup();
}, 5 * 60 * 1000);

export default rateLimiter;
